import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, MessagesSquare } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { dateTimeBR } from "../lib/format";
import { Card, CardHead } from "./common";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

function Bubble({ m, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-[#0B6FC2] text-white rounded-br-sm" : "bg-slate-100 text-slate-900 rounded-bl-sm"}`}>
        <div className={`text-[11px] font-semibold mb-0.5 ${mine ? "text-sky-100" : "text-slate-600"}`}>{m.author} · {m.side === "dpdigi" ? "DPdigi" : "Cliente"}</div>
        <div className="whitespace-pre-wrap break-words">{m.text}</div>
        <div className={`text-[10px] mt-1 text-right font-mono ${mine ? "text-sky-100/80" : "text-slate-400"}`}>{dateTimeBR(m.created_at)}</div>
      </div>
    </div>);
}

export function CompanyChat({ cid, side, title, hint, className = "", compact = false }) {
  const [rows, setRows] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const last = useRef(null);
  const load = useCallback(() => api.get("/chat", { params: { ...(cid ? { company_id: cid } : {}), ...(side === "dpdigi" ? { mark_read: true } : {}) } }).then((r) => setRows(r.data)).catch(() => setRows((x) => x || [])), [cid, side]);
  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 15000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const id = rows?.[rows.length - 1]?.id;
    if (id && id !== last.current && box.current) box.current.scrollTop = box.current.scrollHeight;
    last.current = id;
  }, [rows]);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await api.post("/chat", { company_id: cid, text }); setText(""); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className={`flex flex-col ${className}`} testid="company-chat">
      <CardHead title={title} hint={hint} right={<MessagesSquare className="h-4 w-4 text-[#0B6FC2]" />} />
      <div ref={box} className={`flex-1 ${compact ? "min-h-[150px] max-h-[200px]" : "min-h-[280px] max-h-[520px]"} overflow-y-auto px-4 py-3 space-y-2.5 border-t bg-white`} data-testid="chat-messages">
        {rows === null ? <div className="text-sm text-slate-400">Carregando...</div>
          : rows.length ? rows.map((m) => <Bubble key={m.id} m={m} mine={m.side === side} />)
          : <div className="text-sm text-slate-400 py-8 text-center" data-testid="chat-empty">Nenhuma mensagem ainda. Escreva a primeira.</div>}
      </div>
      <div className="border-t p-3 flex items-end gap-2">
        <Textarea rows={compact ? 1 : 2} value={text} maxLength={4000} onChange={(e) => setText(e.target.value)} placeholder={compact ? "Escreva uma mensagem..." : "Escreva uma mensagem... (Enter envia, Shift+Enter quebra linha)"}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} className="resize-none text-sm" data-testid="chat-input" />
        <Button onClick={send} disabled={busy || !text.trim()} className="shrink-0" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
      </div>
    </Card>);
}
